import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { getAssignmentsData, subscribeToAssignmentListUpdates } from "../../lib/supabase/assignments";
import { createAssignmentListController } from "../../lib/assignmentListController";
import { hasPermission } from "../../lib/permissions";

import {
    FiSearch, FiX, FiAlertCircle, FiDollarSign, FiSend,
    FiCheckCircle, FiClipboard, FiPhone, FiMail, FiMapPin,
    FiUser, FiTool, FiPlus, FiRefreshCw, FiPause,
    FiChevronDown, FiChevronRight, FiEyeOff,
} from "react-icons/fi";
import {
    Home, BrickWall, Grid2X2, Flame,
    ArrowDownNarrowWide, ArrowUpNarrowWide, ArrowDownAZ, ArrowUpAZ,
    PanelsTopLeft,
    SquareStack,
    LineChart,
    ScanLine,
} from "lucide-react";
import { GiBeamWake, GiWoodBeam, GiWoodPile } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import { getJobStatusSummary } from "../../lib/common/jobStatusText";
import { getEnabledExtraQuoteTypes, getQuoteLabel, QUOTE_ORDER } from "../../lib/quoteTypes";
import { BsEmojiAstonished, BsLine } from "react-icons/bs";

// ─── constants ───────────────────────────────────────────────────────────────
const PAGE_LIMIT = 50;

const STORAGE_KEYS = {
    statusFilter: 'assignments_statusFilter',
    hideEstCompleted: 'assignments_hideEstCompleted',
    hideDesignCompleted: 'assignments_hideDesignCompleted',
    jobTypeFilter: 'assignments_jobTypeFilter',
    urgentFilter: 'assignments_urgentFilter',
    quoteSentFilter: 'assignments_quoteSentFilter',
    onHoldFilter: 'assignments_onHoldFilter',
    invoiceFilter: 'assignments_invoiceFilter',
    sortBy: 'assignments_sortBy',
    searchQuery: 'assignments_searchQuery',
    showCancelled: 'assignments_showCancelled',
    beamsFilter: 'assignments_beamsFilter',
    flooringSheetsFilter: 'assignments_flooringSheetsFilter',
};

const load = (key, def) => {
    try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : def; }
    catch { return def; }
};
const save = (key, val) => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { }
};

// ─── status / quote metadata ──────────────────────────────────────────────────

const STATUS_META = {
    pending_design: { label: "Pending Design", dot: "bg-amber-400" },
    in_design: { label: "In Design", dot: "bg-blue-400" },
    design_completed: { label: "Design Completed", dot: "bg-emerald-400" },
    pending_estimation: { label: "Pending Estimation", dot: "bg-orange-400" },
    in_estimation: { label: "In Estimation", dot: "bg-violet-400" },
    estimation_completed: { label: "Estimation Completed", dot: "bg-green-400" },
    ready_for_delivery: { label: "Ready for Delivery", dot: "bg-teal-400" },
    completed: { label: "Completed", dot: "bg-gray-400" },
    cancelled: { label: "Cancelled", dot: "bg-red-500" },
};
const getStatusMeta = (s) => STATUS_META[s] || { label: s?.replace(/_/g, " ") || "—", dot: "bg-gray-300" };

// Quote pill color based on design_status
// completed → green, in_progress → amber, waiting_design → blue, waiting_assignment → gray, not_required → slate
const QUOTE_STATUS_COLORS = {
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    in_progress: "bg-amber-100 text-amber-700 border-amber-200",
    waiting_design: "bg-blue-100 text-blue-700 border-blue-200",
    waiting_assignment: "bg-gray-100 text-gray-500 border-gray-200",
    not_required: "bg-slate-50 text-slate-400 border-slate-200",
};
const getQuoteColor = (designStatus) =>
    QUOTE_STATUS_COLORS[designStatus] || QUOTE_STATUS_COLORS.waiting_assignment;

const QUOTE_ICONS = {
    roof: <Home size={10} />,
    wall: <BrickWall size={10} />,
    floor: <Grid2X2 size={10} />,
    timber: <GiWoodBeam size={10} />,
    beams: <GiWoodPile size={10} />,
    flooring_sheets: <SquareStack size={10} />,
};

// Legend items
const LEGEND = [
    { color: "bg-emerald-100 border-emerald-200 text-emerald-700", label: "Completed" },
    { color: "bg-amber-100 border-amber-200 text-amber-700", label: "In Progress" },
    { color: "bg-blue-100 border-blue-200 text-blue-700", label: "Waiting Design" },
    { color: "bg-gray-100 border-gray-200 text-gray-500", label: "Waiting Assignment" },
];

// Sub-status badge colors (used for estimation/design/production status texts)
const SUB_STATUS_COLORS = {
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    in_progress: "bg-amber-50   text-amber-700   border-amber-200",
    in_estimation: "bg-amber-50   text-amber-700   border-amber-200",
    waiting_design: "bg-blue-50    text-blue-700    border-blue-200",
    waiting_assignment: "bg-gray-100   text-gray-500    border-gray-200",
    not_required: "bg-slate-50   text-slate-400   border-slate-200",
    in_cutting: "bg-orange-50  text-orange-700  border-orange-200",
    cutting_ended: "bg-orange-50  text-orange-700  border-orange-200",
    in_production: "bg-violet-50  text-violet-700  border-violet-200",
    not_started: "bg-gray-100   text-gray-400    border-gray-200",
};
const getSubStatusColor = (status) =>
    SUB_STATUS_COLORS[status] || "bg-gray-50 text-gray-500 border-gray-200";

const SORT_OPTIONS = [
    { value: "newest", label: "Newest First", icon: ArrowDownNarrowWide },
    { value: "oldest", label: "Oldest First", icon: ArrowUpNarrowWide },
    { value: "priority", label: "Priority", icon: Flame },
    { value: "alphabetical", label: "A → Z", icon: ArrowDownAZ },
    { value: "reverse_alphabetical", label: "Z → A", icon: ArrowUpAZ },
];

// ─── tiny helpers ─────────────────────────────────────────────────────────────

const formatDate = (ds) => {
    if (!ds) return "—";
    return new Date(ds).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};
const formatRelativeTime = (date) => {
    if (!date) return "—";
    const diff = Date.now() - new Date(date);
    const m = Math.floor(diff / 60000), h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (d < 7) return `${d}d ago`;
    return formatDate(date);
};

const Chip = ({ children, className = "" }) => (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border ${className}`}>
        {children}
    </span>
);

const DropdownOption = ({ value, current, label, color, onSelect }) => (
    <button
        onClick={() => onSelect(value)}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium transition-colors ${color} ${current === value ? "font-semibold" : ""}`}
    >
        {label}
        {current === value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70 ml-6 flex-shrink-0" />}
    </button>
);

// ─── main component ───────────────────────────────────────────────────────────

const Assignments = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    // ── data ──
    const [assignments, setAssignments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, hasMore: false });
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loadError, setLoadError] = useState("");
    const controllerRef = useRef(null);

    // ── filters (persisted) ──
    const [searchQuery, setSearchQuery] = useState(() => load(STORAGE_KEYS.searchQuery, ""));
    const [debouncedQuery, setDebouncedQuery] = useState(() => load(STORAGE_KEYS.searchQuery, ""));
    const [statusFilter, setStatusFilter] = useState(() => load(STORAGE_KEYS.statusFilter, "all"));
    const [hideEstCompleted, setHideEstCompleted] = useState(() => load(STORAGE_KEYS.hideEstCompleted, false));
    const [hideDesignCompleted, setHideDesignCompleted] = useState(() => load(STORAGE_KEYS.hideDesignCompleted, false));
    const [jobTypeFilter, setJobTypeFilter] = useState(() => load(STORAGE_KEYS.jobTypeFilter, "all"));
    const [urgentFilter, setUrgentFilter] = useState(() => load(STORAGE_KEYS.urgentFilter, "all"));
    const [quoteSentFilter, setQuoteSentFilter] = useState(() => load(STORAGE_KEYS.quoteSentFilter, "all"));
    const [onHoldFilter, setOnHoldFilter] = useState(() => load(STORAGE_KEYS.onHoldFilter, "all"));
    const [invoiceFilter, setInvoiceFilter] = useState(() => load(STORAGE_KEYS.invoiceFilter, "all"));
    const [sortBy, setSortBy] = useState(() => load(STORAGE_KEYS.sortBy, "newest"));
    const [showCancelled, setShowCancelled] = useState(
        () => load(STORAGE_KEYS.showCancelled, false)
    );

    const enabledExtraQuoteTypes = getEnabledExtraQuoteTypes(company);
    const hasBeams = enabledExtraQuoteTypes.includes("beams");
    const hasFlooringSheets = enabledExtraQuoteTypes.includes("flooring_sheets");

    const [beamsFilter, setBeamsFilter] = useState(() => load(STORAGE_KEYS.beamsFilter, false));
    const [flooringSheetsFilter, setFlooringSheetsFilter] = useState(() => load(STORAGE_KEYS.flooringSheetsFilter, false));

    // ── dropdown state ──
    const [openDropdown, setOpenDropdown] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // ── stable refs (MUST be declared at top level, not in an object literal) ──
    const listRef = useRef(null);
    const debounceTimer = useRef(null);
    const refSort = useRef(null);
    const refStatus = useRef(null);
    const refType = useRef(null);
    const refInvoice = useRef(null);
    const refQuoteSent = useRef(null);
    const refUrgent = useRef(null);
    const refHold = useRef(null);

    // ── debounce search ──
    useEffect(() => {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => setDebouncedQuery(searchQuery), 350);
        return () => clearTimeout(debounceTimer.current);
    }, [searchQuery]);

    // ── persist filters ──
    useEffect(() => { save(STORAGE_KEYS.statusFilter, statusFilter); }, [statusFilter]);
    useEffect(() => { save(STORAGE_KEYS.hideEstCompleted, hideEstCompleted); }, [hideEstCompleted]);
    useEffect(() => { save(STORAGE_KEYS.hideDesignCompleted, hideDesignCompleted); }, [hideDesignCompleted]);
    useEffect(() => { save(STORAGE_KEYS.jobTypeFilter, jobTypeFilter); }, [jobTypeFilter]);
    useEffect(() => { save(STORAGE_KEYS.urgentFilter, urgentFilter); }, [urgentFilter]);
    useEffect(() => { save(STORAGE_KEYS.quoteSentFilter, quoteSentFilter); }, [quoteSentFilter]);
    useEffect(() => { save(STORAGE_KEYS.onHoldFilter, onHoldFilter); }, [onHoldFilter]);
    useEffect(() => { save(STORAGE_KEYS.invoiceFilter, invoiceFilter); }, [invoiceFilter]);
    useEffect(() => { save(STORAGE_KEYS.sortBy, sortBy); }, [sortBy]);
    useEffect(() => { save(STORAGE_KEYS.searchQuery, searchQuery); }, [searchQuery]);
    useEffect(() => {
        save(STORAGE_KEYS.showCancelled, showCancelled);
    }, [showCancelled]);
    useEffect(() => { save(STORAGE_KEYS.beamsFilter, beamsFilter); }, [beamsFilter]);
    useEffect(() => { save(STORAGE_KEYS.flooringSheetsFilter, flooringSheetsFilter); }, [flooringSheetsFilter]);

    // ── close dropdowns on outside click ──
    useEffect(() => {
        const allRefs = [refSort, refStatus, refType, refInvoice, refQuoteSent, refUrgent, refHold];
        const handler = (e) => {
            const isInside = allRefs.some(r => r.current?.contains(e.target));
            if (!isInside) setOpenDropdown(null);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const toggleDD = (name) => setOpenDropdown(prev => prev === name ? null : name);

    useLayoutEffect(() => {
        const anchor = { status: refStatus, type: refType, invoice: refInvoice,
            quoteSent: refQuoteSent, urgent: refUrgent, hold: refHold }[openDropdown]?.current;
        const menu = anchor?.querySelector(':scope > .absolute');
        if (!menu) return;
        const positionMenu = () => {
            if (!window.matchMedia('(max-width: 767px)').matches) return;
            const rect = anchor.getBoundingClientRect();
            const width = Math.min(224, window.innerWidth - 32);
            const top = rect.bottom + 6;
            menu.style.setProperty('--filter-top', `${top}px`);
            menu.style.setProperty('--filter-left', `${Math.max(16, Math.min(rect.left, window.innerWidth - width - 16))}px`);
            menu.style.setProperty('--filter-height', `${Math.max(44, window.innerHeight - top - 90)}px`);
        };
        positionMenu();
        window.addEventListener('resize', positionMenu);
        window.addEventListener('scroll', positionMenu, true);
        return () => {
            window.removeEventListener('resize', positionMenu);
            window.removeEventListener('scroll', positionMenu, true);
        };
    }, [openDropdown]);


    // ── fetch helpers ──
    const buildFilters = useCallback(() => ({
        searchQuery: debouncedQuery,
        statusFilter,
        hideEstCompleted,
        hideDesignCompleted,
        jobTypeFilter,
        urgentFilter,
        quoteSentFilter,
        onHoldFilter,
        invoiceFilter,
        sortBy,
        showCancelled,
        beamsFilter: hasBeams ? beamsFilter : false,
        flooringSheetsFilter: hasFlooringSheets ? flooringSheetsFilter : false,
    }), [debouncedQuery, statusFilter, hideEstCompleted, hideDesignCompleted,
        jobTypeFilter, urgentFilter, quoteSentFilter, onHoldFilter, invoiceFilter, sortBy, showCancelled, beamsFilter,
        flooringSheetsFilter,
        hasBeams,
        hasFlooringSheets]);

    const fetchData = useCallback(() => controllerRef.current?.refresh(), []);
    const loadMore = useCallback(() => controllerRef.current?.loadMore(), []);

    useEffect(() => {
        if (!company?.id) return;
        setAssignments([]);
        setPagination({ page: 1, limit: PAGE_LIMIT, total: 0, hasMore: false });
        setLastUpdated(null);
        const filters = buildFilters();
        const controller = createAssignmentListController({
            fetchPage: (page, limit) => getAssignmentsData(company.id, page, limit, filters),
            publish: (state) => {
                if (state.data) setAssignments(state.data);
                if (state.pagination) setPagination(state.pagination);
                if (state.updated) setLastUpdated(state.updated);
                if ('loading' in state) setIsLoading(state.loading);
                if ('loadingMore' in state) setIsLoadingMore(state.loadingMore);
                if ('error' in state) setLoadError(state.error);
            },
        });
        controllerRef.current = controller;
        controller.refresh();
        return () => {
            controller.dispose();
            if (controllerRef.current === controller) controllerRef.current = null;
        };
    }, [company?.id, buildFilters]);

    // Burst events share one refresh; callbacks always use the current filters.
    useEffect(() => {
        if (!company?.id) return;
        let timer;
        let active = true;
        const refresh = () => {
            if (!active) return;
            clearTimeout(timer);
            timer = setTimeout(fetchData, 200);
        };
        const channel = subscribeToAssignmentListUpdates(company.id, refresh);
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);
        return () => {
            active = false;
            clearTimeout(timer);
            window.removeEventListener('focus', onFocus);
            supabase.removeChannel(channel);
        };
    }, [company?.id, fetchData]);

    // ── infinite scroll — attach to the listRef container (must have fixed height + overflow-y-scroll) ──
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (isLoadingMore || !pagination.hasMore) return;
            const threshold = 500;
            const scrolledToBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
            if (scrolledToBottom) loadMore();
        };

        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
        // Re-attach whenever hasMore / loadingMore / loadMore change
    }, [pagination.hasMore, isLoadingMore, loadMore]);

    // ── derived ──
    const activeFilterCount = [
        statusFilter !== "all",
        hideEstCompleted,
        hideDesignCompleted,
        jobTypeFilter !== "all",
        urgentFilter !== "all",
        quoteSentFilter !== "all",
        onHoldFilter !== "all",
        invoiceFilter !== "all",
        showCancelled,
        hasBeams && beamsFilter,
        hasFlooringSheets && flooringSheetsFilter,
    ].filter(Boolean).length;

    const clearAllFilters = () => {
        setStatusFilter("all"); setHideEstCompleted(false); setHideDesignCompleted(false); setJobTypeFilter("all");
        setUrgentFilter("all"); setQuoteSentFilter("all"); setOnHoldFilter("all");
        setInvoiceFilter("all"); setSearchQuery(""); setSortBy("newest"); setShowCancelled(false); setBeamsFilter(false);
        setFlooringSheetsFilter(false);
    };

    const currentSort = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
    const SortIcon = currentSort.icon;

    // ── render helpers ──────────────────────────────────────────────────────

    // Colorful quote pills — color driven by design_status
    const renderQuoteChips = (job) => {
        const required = job.job_quotes?.filter(q => q.is_required) || [];
        // group by quote_type
        const grouped = required.reduce((acc, q) => {
            if (!acc[q.quote_type]) acc[q.quote_type] = [];
            acc[q.quote_type].push(q);
            return acc;
        }, {});

        const QUOTE_ICONS_STACK = {
            roof: <Home size={14} />,
            wall: <PanelsTopLeft size={14} />,
            floor: <HiOutlineSquare3Stack3D size={14} />,
            timber: <GiWoodBeam size={14} />,
            beams: <GiWoodPile size={14} />,
            flooring_sheets: <SquareStack size={14} />,
        };

        return Object.entries(grouped)
            .sort(([typeA, quotesA], [typeB, quotesB]) => {
                const typeOrder =
                    (QUOTE_ORDER[typeA] ?? 99) - (QUOTE_ORDER[typeB] ?? 99);

                if (typeOrder !== 0) return typeOrder;

                const aChild = quotesA[0]?.child_unique_id || '';
                const bChild = quotesB[0]?.child_unique_id || '';

                return aChild.localeCompare(bChild);
            }).map(([type, quotes]) => {
                const count = quotes.length;

                return (
                    <div key={type} className="flex items-center gap-1 mr-2">
                        {/* stacked icons */}
                        <div className="flex -space-x-[10px] bg-transparent">
                            {quotes.map((q, i) => (
                                <span
                                    key={i}
                                    className={`p-1 rounded-full ${getQuoteColor(q.design_status)} bg-zinc-100 drop-shadow-sm border-2 border-white`} // ${getQuoteColor(q.design_status)}
                                    style={{
                                        left: i * 12,
                                        zIndex: count - i   // 👈 first icon gets highest z-index
                                    }}
                                >
                                    {QUOTE_ICONS_STACK[type] || <FiClipboard size={14} />}
                                </span>
                            ))}
                        </div>

                        {/* count */}
                        <span className="text-xs text-gray-500">
                            ({count})
                        </span>
                    </div>
                );
            });
    };

    // Invoice summary badge
    const renderInvoiceBadge = (job) => {
        const req = job.job_quotes?.filter(q => q.is_required) || [];
        if (!req.length) return null;
        const inv = req.filter(q => q.invoiced).length;
        if (!inv) return null;
        if (inv === req.length)
            return <Chip className="bg-white text-emerald-700 border-emerald-200">All Invoiced</Chip>;
        return <Chip className="bg-white text-amber-700 border-amber-200">{inv}/{req.length} Invoiced</Chip>;
    };

    // Multi-status row: Estimation (if applicable), Design, Production (if design job)
    const renderStatusFields = (job) => {
        const jobType = job.job_type || "";
        const hasEstimation = jobType.includes("Estimation");
        const hasDesign = jobType.includes("Design") && !jobType.includes("Estimation") || jobType === "Design & Estimation";

        // Resolve statuses from job_quotes
        const req = job.job_quotes?.filter(q => q.is_required) || [];

        const agg = (key) => {
            const vals = req.map(q => q[key]).filter(Boolean);
            if (!vals.length) return null;
            // priority: in_progress > waiting_* > completed > not_required
            const priority = ['in_progress', 'waiting_assignment', 'waiting_design', 'in_cutting', 'cutting_ended', 'in_production', 'completed', 'not_required'];
            return vals.reduce((best, cur) => {
                const bi = priority.indexOf(best), ci = priority.indexOf(cur);
                return (ci === -1 ? priority.length : ci) < (bi === -1 ? priority.length : bi) ? cur : best;
            });
        };

        const fields = [];

        if (job.is_cancelled) {
            fields.push({ label: "Status.", status: "Cancelled", text: "Cancelled", containerStyle: "bg-red-500 border-red-600 text-white", textStyle: "!text-white" });
        }

        if (hasEstimation) {
            const est = agg("estimation_status");
            if (est && est !== "not_required") {
                fields.push({ label: "Est.", status: getJobStatusSummary(job, 'estimation'), text: getJobStatusSummary(job, 'estimation') });
            }
        }
        if (jobType.includes("Design") || jobType === "Timber") {
            const des = agg("design_status");
            if (des && des !== "not_required") {
                fields.push({ label: "Design", status: des, text: des.replace(/_/g, " ") });
            }
            const prod = agg("production_status");
            if (prod && prod !== "not_required" && prod !== "not_started") {
                fields.push({ label: "Prod.", status: prod, text: prod.replace(/_/g, " ") });
            }
        }

        if (!fields.length) return null;

        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {fields.map((f, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${getSubStatusColor(f.status)} ${f?.containerStyle || ""}`}>
                        <span className={`text-[9px] font-bold opacity-60 uppercase ${f?.textStyle || ""}`}>{f.label}</span>
                        {f.text.charAt(0).toUpperCase() + f.text.slice(1)}
                    </span>
                ))}
            </div>
        );
    };

    // Assignees inline text
    const renderAssignees = (job) => {
        const parts = [];
        const byType = {};
        (job.job_designers || []).forEach(d => {
            if (!byType[d.quote_type]) byType[d.quote_type] = [];
            byType[d.quote_type].push(d.name);
        });
        Object.entries(byType).forEach(([t, names]) => parts.push(`${getQuoteLabel(t)}: ${names.join(", ")}`));
        const est = job.job_estimators?.[0];
        if (est) {
            const name = est.estimator_user?.full_name || est.estimator_user?.username || est.name;
            if (name) parts.push(`Est: ${name}`);
        }
        if (!parts.length) return <span className="text-gray-400 text-xs">Unassigned</span>;
        return <span className="text-gray-500 text-xs">{parts.join(" · ")}</span>;
    };

    // ─────────────────────────────── render ─────────────────────────────────

    return (
        <div className="assignments-page flex flex-col h-full min-h-0">

            {/* ── Page header ─────────────────────────────────────────────── */}
            <div className="assignment-header flex items-center justify-between mb-4 flex-shrink-0">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 leading-tight">Assignments</h1>
                    {lastUpdated && (
                        <p className="text-xs text-gray-400 mt-0.5">Updated {formatRelativeTime(lastUpdated)}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={fetchData} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Refresh">
                        <FiRefreshCw size={14} />
                    </button>
                    {hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin"]) && (
                        <button
                            onClick={() => navigate(`/${companySlug}/assignment/new`, {
                                state: { from: location.pathname }
                            })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition-colors"
                        >
                            <FiPlus size={14} /> New
                        </button>
                    )}
                </div>
            </div>

            {/* ── Toolbar ──────────────────────────────────────────────────── */}
            <div className="assignment-toolbar bg-white border border-gray-200 rounded-lg shadow-sm mb-3 flex-shrink-0">

                {/* Row 1 — Search + Sort + Clear */}
                <div className="assignment-search-row flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
                    <div className="assignment-search relative flex-1">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                            type="text"
                            placeholder="Search job ID, customer, supervisor, address…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <FiX size={13} />
                            </button>
                        )}
                    </div>

                    {/* Sort */}
                    <div className="assignment-sort-control relative" ref={refSort}>
                        <button
                            onClick={() => toggleDD("sort")}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-gray-700 border-gray-200 hover:border-gray-300 transition-all"
                        >
                            <SortIcon className="w-3 h-3" />
                            <span className="assignment-sort-label hidden sm:inline">{currentSort.label}</span>
                            <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "sort" ? "rotate-180" : ""}`} />
                        </button>
                        {openDropdown === "sort" && (
                            <div className="absolute right-0 mt-1.5 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                <div className="p-3 border-b border-gray-100">
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Sort By</span>
                                </div>
                                <div className="p-1.5 space-y-0.5">
                                    {SORT_OPTIONS.map(opt => {
                                        const Icon = opt.icon;
                                        return (
                                            <button
                                                key={opt.value}
                                                onClick={() => { setSortBy(opt.value); setOpenDropdown(null); }}
                                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs transition-colors
                                                    ${sortBy === opt.value ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"}`}
                                            >
                                                <Icon className="w-3.5 h-3.5 shrink-0" />
                                                <span className="flex-1 text-left font-medium">{opt.label}</span>
                                                {sortBy === opt.value && (
                                                    <div className="w-7 h-4 rounded-full bg-blue-500 shrink-0">
                                                        <div className="w-3 h-3 bg-white rounded-full shadow mt-0.5 translate-x-3.5" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <button type="button" className="md:hidden flex items-center gap-2 text-sm font-semibold text-gray-700" aria-expanded={mobileFiltersOpen} aria-controls="assignment-filter-options" onClick={() => { setMobileFiltersOpen(v => !v); setOpenDropdown(null); }}>
                        Filters{activeFilterCount > 0 && <span className="rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-xs">{activeFilterCount}</span>}
                        <FiChevronDown className={mobileFiltersOpen ? 'rotate-180' : ''} />
                    </button>

                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearAllFilters}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded border border-red-200 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                            <FiX size={11} /> Clear ({activeFilterCount})
                        </button>
                    )}
                </div>

                {/* Row 2 — filter header */}
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 hidden md:flex items-center justify-between">
                    <span className="hidden md:inline text-xs font-semibold text-gray-500 uppercase tracking-wide">Filters</span>

                    {activeFilterCount > 0 && (
                        <button onClick={clearAllFilters} className="text-xs text-blue-600 hover:text-blue-800 transition-colors font-medium">
                            Reset all
                        </button>
                    )}
                </div>

                {/* Row 3 — filter dropdowns (same pattern as JobEditorTable) */}
                <div id="assignment-filter-options" className={`assignment-filter-options ${mobileFiltersOpen ? "is-open" : ""} flex items-start gap-4 flex-wrap px-4 py-3`}>

                    {/* ── Status ── */}
                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-gray-500">Status:</span>
                        <div className="flex flex-row gap-2 flex-wrap">
                            <div className="relative" ref={refStatus}>
                                <button
                                    onClick={() => toggleDD("status")}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                                        ${statusFilter !== "all" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                                >
                                    Status: {statusFilter === "all" ? "All" : getStatusMeta(statusFilter).label}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "status" ? "rotate-180" : ""}`} />
                                </button>
                                {openDropdown === "status" && (
                                    <div className="absolute left-0 mt-1.5 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[{ value: "all", label: "All Statuses", dot: null },
                                            ...Object.entries(STATUS_META).map(([v, m]) => ({ value: v, label: m.label, dot: m.dot }))
                                            ].map(opt => (
                                                <button key={opt.value}
                                                    onClick={() => { setStatusFilter(opt.value); setOpenDropdown(null); }}
                                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors
                                                        hover:bg-gray-50 text-gray-700 ${statusFilter === opt.value ? "font-semibold bg-blue-50 !text-blue-700" : ""}`}
                                                >
                                                    {opt.dot && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${opt.dot}`} />}
                                                    <span className="flex-1 text-left">{opt.label}</span>
                                                    {statusFilter === opt.value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Hide completed toggle */}
                            {/* Hide estimation completed */}
                            <button
                                onClick={() => setHideEstCompleted(v => !v)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
        ${hideEstCompleted ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                            >
                                <FiEyeOff className="w-3 h-3" />
                                {hideEstCompleted ? "Est. Hidden" : "Hide Est. Done"}
                            </button>

                            {/* Hide design completed */}
                            <button
                                onClick={() => setHideDesignCompleted(v => !v)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
        ${hideDesignCompleted ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                            >
                                <FiEyeOff className="w-3 h-3" />
                                {hideDesignCompleted ? "Design Hidden" : "Hide Design Done"}
                            </button>


                            <button
                                onClick={() => setShowCancelled(v => !v)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
        ${showCancelled
                                        ? "bg-red-50 text-red-700 border-red-200"
                                        : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                                    }`}
                            >
                                <FiEyeOff className="w-3 h-3" />
                                {showCancelled ? "Showing Cancelled" : "Hide Cancelled"}
                            </button>
                        </div>
                    </div>

                    <div className="w-px h-10 bg-gray-200 self-center" />

                    {/* ── Job Type ── */}
                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-gray-500">Job Type:</span>
                        <div className="relative" ref={refType}>
                            <button
                                onClick={() => toggleDD("type")}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                                    ${jobTypeFilter !== "all" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                            >
                                Type: {
                                    jobTypeFilter === "all" ? "All" :
                                        jobTypeFilter === "design_only" ? "Design" :
                                            jobTypeFilter === "estimation_only" ? "Estimation" :
                                                jobTypeFilter === "design_estimation_only" ? "D & E" : "Timber"
                                }
                                <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "type" ? "rotate-180" : ""}`} />
                            </button>
                            {openDropdown === "type" && (
                                <div className="absolute left-0 mt-1.5 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                    <div className="p-1.5 space-y-0.5">
                                        {[
                                            { value: "all", label: "All Types", color: "hover:bg-gray-50 text-gray-700" },
                                            { value: "design_only", label: "Design Only", color: "hover:bg-teal-50 text-teal-700" },
                                            { value: "estimation_only", label: "Estimation Only", color: "hover:bg-cyan-50 text-cyan-700" },
                                            { value: "design_estimation_only", label: "Design & Estimation", color: "hover:bg-emerald-50 text-emerald-700" },
                                            { value: "timber_only", label: "Timber Only", color: "hover:bg-lime-50 text-lime-700" },
                                        ].map(opt => (
                                            <DropdownOption key={opt.value} value={opt.value} current={jobTypeFilter} label={opt.label} color={opt.color}
                                                onSelect={v => { setJobTypeFilter(v); setOpenDropdown(null); }} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="w-px h-10 bg-gray-200 self-center" />

                    {/* ── Invoice + Quote Sent + Urgent + Hold ── */}
                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-gray-500">More Filters:</span>
                        <div className="flex flex-row gap-2 flex-wrap">

                            {/* Invoice */}
                            <div className="relative" ref={refInvoice}>
                                <button
                                    onClick={() => toggleDD("invoice")}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                                        ${invoiceFilter === "all_invoiced" ? "bg-green-50 text-green-700 border-green-200" :
                                            invoiceFilter === "partial" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                                                invoiceFilter === "not_invoiced" ? "bg-red-50 text-red-700 border-red-200" :
                                                    "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                                >
                                    <FiDollarSign className="w-3 h-3" />
                                    Invoice: {
                                        invoiceFilter === "all" ? "All" :
                                            invoiceFilter === "all_invoiced" ? "All Invoiced" :
                                                invoiceFilter === "partial" ? "Partial" : "Not Invoiced"
                                    }
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "invoice" ? "rotate-180" : ""}`} />
                                </button>
                                {openDropdown === "invoice" && (
                                    <div className="absolute left-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: "all", label: "All", color: "hover:bg-gray-50 text-gray-700" },
                                                { value: "all_invoiced", label: "All Invoiced", color: "hover:bg-green-50 text-green-700" },
                                                { value: "partial", label: "Partial", color: "hover:bg-yellow-50 text-yellow-700" },
                                                { value: "not_invoiced", label: "Not Invoiced", color: "hover:bg-red-50 text-red-700" },
                                            ].map(opt => (
                                                <DropdownOption key={opt.value} value={opt.value} current={invoiceFilter} label={opt.label} color={opt.color}
                                                    onSelect={v => { setInvoiceFilter(v); setOpenDropdown(null); }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Quote Sent */}
                            <div className="relative" ref={refQuoteSent}>
                                <button
                                    onClick={() => toggleDD("quoteSent")}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                                        ${quoteSentFilter === "sent" ? "bg-green-50 text-green-700 border-green-200" :
                                            quoteSentFilter === "not_sent" ? "bg-red-50 text-red-700 border-red-200" :
                                                "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                                >
                                    <FiSend className="w-3 h-3" />
                                    Quote: {quoteSentFilter === "all" ? "All" : quoteSentFilter === "sent" ? "Sent" : "Not Sent"}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "quoteSent" ? "rotate-180" : ""}`} />
                                </button>
                                {openDropdown === "quoteSent" && (
                                    <div className="absolute left-0 mt-1.5 w-40 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: "all", label: "All", color: "hover:bg-gray-50 text-gray-700" },
                                                { value: "sent", label: "Sent", color: "hover:bg-green-50 text-green-700" },
                                                { value: "not_sent", label: "Not Sent", color: "hover:bg-red-50 text-red-700" },
                                            ].map(opt => (
                                                <DropdownOption key={opt.value} value={opt.value} current={quoteSentFilter} label={opt.label} color={opt.color}
                                                    onSelect={v => { setQuoteSentFilter(v); setOpenDropdown(null); }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Urgent */}
                            <div className="relative" ref={refUrgent}>
                                <button
                                    onClick={() => toggleDD("urgent")}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                                        ${urgentFilter === "urgent_quote" ? "bg-red-50 text-red-700 border-red-200" :
                                            urgentFilter === "urgent_design" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                                urgentFilter === "urgent_any" ? "bg-orange-50 text-orange-700 border-orange-200" :
                                                    "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                                >
                                    <FiAlertCircle className="w-3 h-3" />
                                    Urgent: {
                                        urgentFilter === "all" ? "All" :
                                            urgentFilter === "urgent_any" ? "Any" :
                                                urgentFilter === "urgent_quote" ? "Quote" : "Design"
                                    }
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "urgent" ? "rotate-180" : ""}`} />
                                </button>
                                {openDropdown === "urgent" && (
                                    <div className="absolute left-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: "all", label: "All", color: "hover:bg-gray-50 text-gray-700" },
                                                { value: "urgent_any", label: "Any Urgent", color: "hover:bg-orange-50 text-orange-700" },
                                                { value: "urgent_quote", label: "Urgent Quote", color: "hover:bg-red-50 text-red-700" },
                                                { value: "urgent_design", label: "Urgent Design", color: "hover:bg-amber-50 text-amber-700" },
                                            ].map(opt => (
                                                <DropdownOption key={opt.value} value={opt.value} current={urgentFilter} label={opt.label} color={opt.color}
                                                    onSelect={v => { setUrgentFilter(v); setOpenDropdown(null); }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* On Hold */}
                            <div className="relative" ref={refHold}>
                                <button
                                    onClick={() => toggleDD("hold")}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                                        ${onHoldFilter === "on_hold" ? "bg-gray-200 text-gray-700 border-gray-400" :
                                            onHoldFilter === "active" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                                "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                                >
                                    <FiPause className="w-3 h-3" />
                                    Hold: {onHoldFilter === "all" ? "All" : onHoldFilter === "on_hold" ? "On Hold" : "Active"}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "hold" ? "rotate-180" : ""}`} />
                                </button>
                                {openDropdown === "hold" && (
                                    <div className="absolute left-0 mt-1.5 w-36 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: "all", label: "All", color: "hover:bg-gray-50 text-gray-700" },
                                                { value: "on_hold", label: "On Hold", color: "hover:bg-gray-100 text-gray-700" },
                                                { value: "active", label: "Active", color: "hover:bg-blue-50 text-blue-700" },
                                            ].map(opt => (
                                                <DropdownOption key={opt.value} value={opt.value} current={onHoldFilter} label={opt.label} color={opt.color}
                                                    onSelect={v => { setOnHoldFilter(v); setOpenDropdown(null); }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={`flex flex-col gap-2 ${!hasBeams && !hasFlooringSheets ? "assignment-empty-departments" : ""}`}>
                        <span className="text-xs font-medium text-gray-500">Addition Departments:</span>
                        <div className="flex flex-row gap-2 flex-wrap">
                            {hasBeams && (
                                <button
                                    onClick={() => setBeamsFilter(v => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
            ${beamsFilter
                                            ? "bg-orange-50 text-orange-700 border-orange-200"
                                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                                        }`}
                                >
                                    <GiWoodPile className="w-3 h-3" />
                                    Beams
                                </button>
                            )}

                            {hasFlooringSheets && (
                                <button
                                    onClick={() => setFlooringSheetsFilter(v => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
            ${flooringSheetsFilter
                                            ? "bg-violet-50 text-violet-700 border-violet-200"
                                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                                        }`}
                                >
                                    <SquareStack className="w-3 h-3" />
                                    Flooring Sheets
                                </button>
                            )}
                        </div>
                    </div>


                </div>

                {/* Quote color legend + result count */}
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 rounded-b-lg flex items-center justify-between flex-wrap gap-2">
                    {/* Legend */}
                    <div className="assignment-legend flex items-center gap-1 flex-wrap">
                        <span className="text-[10px] text-gray-400 font-medium mr-1">Quote:</span>
                        {LEGEND.map(l => (
                            <span key={l.label} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${l.color}`}>
                                {l.label}
                            </span>
                        ))}
                    </div>
                    {/* Count */}
                    <span className="text-xs text-gray-500 ml-auto">
                        Showing <span className="font-semibold text-gray-800">{assignments.length}</span>
                        {" "}of{" "}
                        <span className="font-semibold text-gray-800">{pagination.total}</span> assignments
                        {pagination.hasMore && <span className="ml-2 text-blue-600 font-medium">· scroll for more</span>}
                    </span>
                </div>
            </div>

            {loadError && <div role="alert" className="p-3 text-sm text-red-700 bg-red-50">{loadError} <button onClick={fetchData} className="underline">Retry</button></div>}

            {/* ── List — fixed height + overflow-y-scroll so scroll events fire correctly ── */}
            <div
                ref={listRef}
                className="assignment-list flex-1 h-[90vh] overflow-y-scroll"

            >
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                        <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-500">Loading assignments…</p>
                    </div>
                ) : assignments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <FiClipboard className="text-gray-400" size={20} />
                        </div>
                        <p className="text-sm font-medium text-gray-700 mb-1">No assignments found</p>
                        <p className="text-xs text-gray-400">
                            {activeFilterCount > 0 || searchQuery ? "Try adjusting filters or search" : "No assignments created yet"}
                        </p>
                        {(activeFilterCount > 0 || searchQuery) && (
                            <button onClick={clearAllFilters} className="mt-3 text-xs text-blue-600 hover:underline font-medium">Clear all filters</button>
                        )}
                    </div>
                ) : (
                    <div className="assignment-cards space-y-1.5 pb-6 h-[90vh] border rounded-lg py-2 px-2">
                        {assignments.map(job => {
                            const sm = getStatusMeta(job.main_status);
                            const isUrgent = job.urgent_quote || job.urgent_design;

                            return (
                                <div
                                    key={job.id}
                                    onClick={() => navigate(`/${companySlug}/assignment/${job.id}`, {
                                        state: { from: location.pathname }
                                    })}
                                    className="assignment-card group bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                                >
                                    <div className="assignment-card-layout flex items-stretch min-h-[80px]">
                                        {/* Left accent */}
                                        <div className={`w-1 rounded-l-lg flex-shrink-0 ${sm.dot}`} />

                                        {/* Content */}
                                        <div className="assignment-card-body flex-1 min-w-0 px-3 py-2.5 flex flex-col gap-1.5">

                                            {/* Row 1 — ID + status + badges */}
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-sm text-gray-900 tracking-tight">{job.unique_id}</span>
                                                {/* <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sm.dot}`} />
                                                    {sm.label}
                                                </span> */}
                                                <span className="text-xs text-gray-400 font-medium">{job.job_type}</span>
                                                {job.on_hold && <Chip className="bg-gray-200 text-gray-600 border-gray-300"><FiPause size={9} /> On Hold</Chip>}
                                                {isUrgent && (
                                                    <Chip className="bg-red-100 text-red-700 border-red-200">
                                                        <FiAlertCircle size={9} />
                                                        {job.urgent_quote && job.urgent_design ? "Urgent" : job.urgent_quote ? "Urgent Quote" : "Urgent Design"}
                                                    </Chip>
                                                )}
                                                {
                                                    (job?.is_unit && job?.unit_count > 0) &&
                                                    <Chip className="bg-white text-blue-700 border-blue-200">{job?.unit_count} Unit{job?.unit_count > 1 && "s"}</Chip>
                                                }
                                                {renderInvoiceBadge(job)}
                                                {job.quote_sent && <Chip className="bg-blue-50 text-blue-700 border-blue-200"><FiSend size={9} /> Sent</Chip>}
                                            </div>

                                            {/* Row 2 — customer + supervisor + address */}
                                            <div className="assignment-customer flex items-center gap-4 flex-wrap text-xs">
                                                <span className="flex items-center gap-1 font-medium text-gray-700">
                                                    <FiUser size={11} className="text-gray-400 flex-shrink-0" />
                                                    {job.customer_name || "—"}
                                                </span>
                                                {job.carpenter_supervisor_name && (
                                                    <span className="flex items-center gap-1 text-gray-500">
                                                        <FiTool size={11} className="text-gray-400 flex-shrink-0" />
                                                        {job.carpenter_supervisor_name}
                                                    </span>
                                                )}
                                                {job.customer_address && (
                                                    <span className="flex items-center gap-1 text-gray-400 max-w-xs truncate">
                                                        <FiMapPin size={11} className="flex-shrink-0" />
                                                        {job.customer_address}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Row 3 — quote chips + assignees */}
                                            {job?.job_type?.toLowerCase()?.includes("design") &&
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {renderQuoteChips(job)}
                                                    {/* {job.job_quotes?.some(q => q.is_required) && <span className="text-gray-200 text-xs">|</span>}
                                                {renderAssignees(job)} */}
                                                </div>
                                            }

                                            {/* Row 4 — estimation / design / production status badges */}
                                            {renderStatusFields(job)}
                                        </div>

                                        {/* Right side */}
                                        <div className="assignment-card-meta flex flex-col items-end justify-between py-2.5 pr-3 pl-2 flex-shrink-0 gap-2">
                                            <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDate(job.created_at)}</span>
                                            <div className="assignment-contact-actions flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {job.customer_number && (
                                                    <a href={`tel:${job.customer_number}`} onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600 transition-colors" title="Call"><FiPhone size={13} /></a>
                                                )}
                                                {job.customer_mail && (
                                                    <a href={`mailto:${job.customer_mail}`} onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600 transition-colors" title="Email"><FiMail size={13} /></a>
                                                )}
                                                {job.customer_address && (
                                                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.customer_address)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600 transition-colors" title="Map"><FiMapPin size={13} /></a>
                                                )}
                                            </div>
                                            <FiChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {isLoadingMore && (
                            <div className="flex items-center justify-center py-4 gap-2">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs text-gray-500">Loading more…</span>
                            </div>
                        )}
                        {!isLoadingMore && !pagination.hasMore && assignments.length > 0 && (
                            <p className="text-center text-xs text-gray-400 py-3">All {pagination.total} assignments loaded</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Assignments;